import { dataSlice as createDataSLice } from '../../../core/data';
import { sliceList as createListSlice } from '../../../core/list';
import { sliceFilter as createFilterSlice } from '../../../core/filter';
import { sliceSearch as createSearchSlice } from '../../../core/search';
import { detailSlice as createDetailSlice } from '../../../core/detail';
import {
  combineReducers,
  createAction,
  createSelector,
} from '@reduxjs/toolkit';

import { ListConf } from '../conf/List.conf';
import { SearchConf } from '../conf/Search.conf';
import { FilterConf } from '../conf/Filter.conf';
import { DataConf } from '../conf/Data.conf';
import { DetailConf } from '../conf/Detail.conf';
import _ from 'lodash';
import { QueryConf } from '../../conf/Query.conf';

export const tag = 'contacts_suppliers';

// REDUCERS
const dataSlice = createDataSLice(`${tag}/data`, DataConf.initialState);
const listSlice = createListSlice(`${tag}/list`, ListConf.initialState);
const filterSlice = createFilterSlice(`${tag}/filter`, FilterConf.initialState);
const searchSlice = createSearchSlice(`${tag}/search`, SearchConf.initialState);
const detailSlice = createDetailSlice(`${tag}/detail`, DetailConf.initialState);

export const reducer = combineReducers({
  search: searchSlice.reducer,
  data: dataSlice.reducer,
  list: listSlice.reducer,
  filter: filterSlice.reducer,
  detail: detailSlice.reducer,
});

// ACTIONS
export const actions = {
  fetchAllAction: createAction(`${tag}/fetchAll`),
  fetchDetailAction: createAction(`${tag}/fetchOne`),
  ...searchSlice.actions,
  ...dataSlice.actions,
  ...listSlice.actions,
  ...filterSlice.actions,
  ...detailSlice.actions,
};

// QUERY
export const querySelector = createSelector(
  (state) => state[tag].list,
  (state) => state[tag].filter,
  (state) => state[tag].search,
  (list, filter, search) =>
    QueryConf.get(list, filter, search, FilterConf, SearchConf)
);

// LIST
export const listSelector = createSelector(
  (state) => state[tag].data,
  (state) => state[tag].list,
  ({ data, meta }, { pagination, sort }) => ({
    data,
    meta,
    pagination,
    sort,
  })
);

// DETAIL
export const detailSelector = createSelector(
  (state) => state[tag].detail,
  ({ data, loading, error }) => {
    return {
      loading,
      error,
      data: {
        ...data,
      },
    };
  }
);

// EDIT
export const editSelector = createSelector(
  (state) => state[tag].detail,
  ({ data, loading, error }) => {
    const priority = _.get(data, 'attributes.priority');
    const departments = _.get(data, 'attributes.id_departments.data', []);
    const target = _.get(data, 'attributes.target');
    const stars = _.get(data, 'attributes.stars');
    const languages = _.get(data, 'attributes.id_languages.data', []);
    const services_on_board = _.get(data, 'attributes.services_on_board');
    const locations = _.get(data, 'attributes.id_locations.data', []);
    const categories = _.get(data, 'attributes.id_category.data', []);
    const subCategories = _.get(data, 'attributes.id_sub_category.data', []);
    const supplierContacts = _.get(data, 'attributes.supplier_contacts', []);

    return {
      loading,
      error,
      data: data
        ? {
          priority: priority ? { label: priority, id: priority } : null,
          id_departments: departments
            ? departments.map((dep) => ({
              name: dep.attributes.name_department,
              id: dep.id,
            }))
            : [],
          supplier_contacts: Array.isArray(supplierContacts)
            ? supplierContacts.map((contact) => ({
              id: contact?.id || null,
              main_contact: contact?.main_contact || '',
              main_contact_phone_number: contact?.main_contact_phone_number || '',
              main_contact_email: contact?.main_contact_email || null,
            }))
            : [],
          status: _.get(data, 'attributes.status'),
          opening_date: _.get(data, 'attributes.opening_date'),
          closing_date: _.get(data, 'attributes.closing_date'),
          id_languages: languages
            ? languages.map((lang) => ({
              name: `${lang.attributes.nation} - ${lang.attributes.language}`,
              id: lang.id,
            }))
            : [],
          target: target ? { label: target, id: target } : null,
          details: _.get(data, 'attributes.details'),
          stars: stars ? { label: stars, id: stars } : null,
          working_hours: _.get(data, 'attributes.working_hours'),
          services_on_board: services_on_board === 'Yes' ? true : false,
          fares: _.get(data, 'attributes.fares'),
          commissions: _.get(data, 'attributes.commissions'),
          cancellation_fees: _.get(data, 'attributes.cancellation_fees'),
          notes: _.get(data, 'attributes.notes'),
          rules: _.get(data, 'attributes.rules'),
          other_contacts: _.get(data, 'attributes.other_contacts'),
          bank_details: _.get(data, 'attributes.bank_details'),
          id_locations: locations
            ? locations.map((loc) => ({
              name: `${loc.attributes.location}${loc.attributes.marina ? ` - ${loc.attributes.marina}` : ''}${loc.attributes.area ? ` - ${loc.attributes.area}` : ''}`,
              id: loc.id,
            }))
            : [],
          // disabled fields
          label: _.get(data, 'attributes.label'),
          category: _.get(data, 'attributes.category'),
          sub_category: _.get(data, 'attributes.sub_category'),
          address: _.get(data, 'attributes.address'),
          mexal_code: _.get(data, 'attributes.mexal_code'),
          year: _.get(data, 'attributes.year'),
          company_name: _.get(data, 'attributes.company_name'),
          vat_number: _.get(data, 'attributes.vat_number'),
          sdi_code: _.get(data, 'attributes.sdi_code'),
          phone_number: _.get(data, 'attributes.phone_number'),
          website: _.get(data, 'attributes.website'),
          email: _.get(data, 'attributes.email'),
          id_category: categories
            ? categories.map((category) => ({
              name: category?.attributes?.name,
              id: category?.id,
            }))
            : [],
          id_sub_category: subCategories
            ? subCategories.map((subCategory) => ({
              name: subCategory?.attributes?.name,
              id: subCategory?.id,
            }))
            : [],
        }
        : null,
    };
  }
);
